import type { SmartFormConfig } from '@pesto/ui/features/SmartForm/types';

import { useMutationWithToast } from '@pesto/ui';
import { humanize } from '@pesto/utils/src/string/stringHelper';
import { z } from 'zod';

import type { CardModalPayload, ModalProps } from '../types/modal.types';

import { BlockUnblockCardAction, useBlockUnblockCardMutation, useGetUserQuery } from '../../__generated__/graphql/api';
import { getUserRefetchQuery } from '../../components/UserDetails/getUserRefetchQuery';
import { createFormComponent } from '../utils/create-form-component';

const schema = z
  .object({
    cardId: z.string().min(1, 'Card ID is required'),
    action: z.nativeEnum(BlockUnblockCardAction, { required_error: 'Action is required' }),
    blockReason: z.string().optional(),
  })
  .refine(
    (data) => {
      if (data.action === BlockUnblockCardAction.Block && !data.blockReason?.trim()) {
        return false;
      }

      return true;
    },
    {
      message: 'Block reason is required when blocking a card',
      path: ['blockReason'],
    },
  );

export const BlockUnblockCardActionComponent = ({ payload, api }: ModalProps<CardModalPayload>) => {
  const { userId = '', cardId } = payload;

  const { data } = useGetUserQuery({
    variables: {
      userId,
    },
    skip: !userId || !!cardId,
  });

  const { handleMutation } = useMutationWithToast();
  const refetchQuery = getUserRefetchQuery(userId);
  const [blockUnblockCard] = useBlockUnblockCardMutation(refetchQuery);

  const handleSubmit = (data: z.infer<typeof schema>) => {
    void handleMutation({
      mutation: () =>
        blockUnblockCard({
          variables: {
            cardId: data.cardId,
            action: data.action,
            blockReason: data.blockReason,
          },
        }),
      extractSuccess: (result) => !!result?.data,
      onSuccess: () => api.close(),
      debugLabel: 'block-unblock-card',
      toast: {
        success: `Card ${data.action.toLowerCase()}ed successfully`,
        error: `Failed to ${data.action.toLowerCase()} card`,
        loading: `${data.action}ing card...`,
      },
    });
  };

  const user = data?.user;

  const activeCard = data?.user?.cards?.find((card) => card?.active);
  const resolvedCardId = cardId ?? activeCard?.id ?? '';

  const config: SmartFormConfig<z.infer<typeof schema>> = {
    fields: [
      cardId
        ? {
            name: 'cardId',
            label: '',
            type: 'text',
            defaultValue: resolvedCardId,
            componentProps: {
              wrapperClassName: 'hidden',
              inputClassName: 'hidden',
            },
          }
        : {
            name: 'cardId',
            label: 'Card',
            type: 'select',
            required: true,
            options:
              user?.cards?.map((card) => ({
                value: card?.id ?? '',
                label: card?.referenceId ?? '',
              })) ?? [],
          },
      {
        name: 'action',
        label: 'Action',
        type: 'select',
        required: true,
        options: Object.values(BlockUnblockCardAction).map((action) => ({
          value: action,
          label: humanize(action),
        })),
      },
      {
        name: 'blockReason',
        label: 'Block Reason',
        type: 'text',
        required: true,
        placeholder: 'Please provide a reason for blocking this card',
        conditional: {
          when: {
            field: 'action',
            operator: 'equals',
            value: BlockUnblockCardAction.Block,
          },
        },
      },
    ],
    schema,
    defaultValues: {
      cardId: resolvedCardId,
      action: BlockUnblockCardAction.Unblock,
      blockReason: '',
    },
  };

  return createFormComponent({
    config,
    handleSubmit,
  });
};
